import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  MenuItem,
  Button,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

const initialFormState = {
  hearing_date: '',
  next_hearing_date: '',
  court_room: '',
  judge_name: '',
  hearing_status: 'Pending', // Default status
  summary: '',
  order_details: ''
};

const HearingDialog = ({ open, handleClose, case_id, case_no, onSave, HearingEditData, isHearingEdit, setIsHearingEdit }) => {
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setFormData({ ...initialFormState });
      setErrors({});
    }
  }, [open]);

  useEffect(() => {
    if (HearingEditData) {
      setFormData({
        hearing_date: HearingEditData.hearing_date
          ? HearingEditData.hearing_date.split('T')[0]
          : '',

        next_hearing_date: HearingEditData.next_hearing_date
          ? HearingEditData.next_hearing_date.split('T')[0]
          : '',

        court_room: HearingEditData.court_room || '',
        judge_name: HearingEditData.judge_name || '',
        hearing_status: HearingEditData.hearing_status || 'Pending',
        summary: HearingEditData.summary || '',
        order_details: HearingEditData.order_details || ''
      });
    }
  }, [HearingEditData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error dynamically when typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Strict Validation Logic matching Laravel rules
  const validateForm = () => {
    let tempErrors = {};

    // 1. Hearing Date Check
    if (!formData.hearing_date) {
      tempErrors.hearing_date = "Hearing date is required.";
    }

    // 2. Next Hearing Date Check (after_or_equal:hearing_date)
    if (formData.next_hearing_date && formData.hearing_date) {
      const hDate = new Date(formData.hearing_date);
      const nDate = new Date(formData.next_hearing_date);
      if (nDate < hDate) {
        tempErrors.next_hearing_date = "Next hearing date must be after or equal to hearing date.";
      }
    }

    // 3. Court Room Max 100
    if (formData.court_room && formData.court_room.length > 100) {
      tempErrors.court_room = "Court room cannot exceed 100 characters.";
    }

    // 4. Judge Name Max 255
    if (formData.judge_name && formData.judge_name.length > 255) {
      tempErrors.judge_name = "Judge name cannot exceed 255 characters.";
    }

    // 5. Hearing Status Check
    if (!formData.hearing_status) {
      tempErrors.hearing_status = "Hearing status is required.";
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      // Final payload incorporating the case_id from props
      const payload = {
        case_id: case_id,
        ...formData
      };
      onSave(payload);
    }
  };

  const handleReset = () => {
    setFormData(initialFormState);
    setErrors({});
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      {/* Dialog Header */}
      <DialogTitle sx={{ m: 0, p: 2, bg: '#1976d2', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#1976d2' }}>
        Add New Hearing (Case No.: #{case_no})
        <IconButton onClick={handleClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Dialog Form Fields Container */}
      <DialogContent dividers sx={{ p: 3 }}>
        <Grid container spacing={2}>

          {/* 1. Hearing Date */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={2}>

              {/* Hearing Date */}
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Last Hearing Date"
                  format="DD/MM/YYYY"
                  value={formData.hearing_date ? dayjs(formData.hearing_date) : null}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      hearing_date: value ? value.format("YYYY-MM-DD") : "",
                    }))
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      error: !!errors.hearing_date,
                      helperText: errors.hearing_date,
                    },
                  }}
                />
              </Grid>

              {/* Next Hearing Date */}
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Next Hearing Date"
                  format="DD/MM/YYYY"
                  value={formData.next_hearing_date ? dayjs(formData.next_hearing_date) : null}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      next_hearing_date: value ? value.format("YYYY-MM-DD") : "",
                    }))
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      error: !!errors.next_hearing_date,
                      helperText: errors.next_hearing_date,
                    },
                  }}
                />
              </Grid>

            </Grid>
          </LocalizationProvider>

          {/* 3. Court Room */}
          <Grid item xs={12} sm={6}>
            <TextField
              name="court_room"
              label="Court Room"
              fullWidth
              size="small"
              value={formData.court_room}
              onChange={handleChange}
              error={!!errors.court_room}
              helperText={errors.court_room}
            />
          </Grid>

          {/* 4. Judge Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              name="judge_name"
              label="Judge Name"
              fullWidth
              size="small"
              value={formData.judge_name}
              onChange={handleChange}
              error={!!errors.judge_name}
              helperText={errors.judge_name}
            />
          </Grid>

          {/* 5. Hearing Status (Select Dropdown) */}
          {/* <Grid item xs={12}>
            <TextField
              select
              name="hearing_status"
              label="Hearing Status"
              fullWidth
              size="small"
              value={formData.hearing_status}
              onChange={handleChange}
              error={!!errors.hearing_status}
              helperText={errors.hearing_status}
            >
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
              <MenuItem value="Adjourned">Adjourned</MenuItem>
              <MenuItem value="Disposed">Disposed</MenuItem>
            </TextField>
          </Grid> */}

          {/* 6. Summary */}
          <Grid item xs={12}>
            <TextField
              name="summary"
              label="Hearing Summary"
              fullWidth
              multiline
              rows={3}
              size="small"
              value={formData.summary}
              onChange={handleChange}
            />
          </Grid>

          {/* 7. Order Details */}
          {/* <Grid item xs={12}>
            <TextField
              name="order_details"
              label="Order Details"
              fullWidth
              multiline
              rows={3}
              size="small"
              value={formData.order_details}
              onChange={handleChange}
            />
          </Grid> */}
        </Grid>
      </DialogContent>

      {/* Action Buttons */}
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<RotateLeftIcon />}
          onClick={handleReset}
          sx={{ borderRadius: 2 }}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          sx={{ borderRadius: 2 }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HearingDialog;